import React, { useState } from 'react';
import {
  Box, Container, Typography, Button,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

export interface FAQItem {
  question: string;
  answer: string;
}

export const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'TacticaFoot est-il adapté aux entraîneurs amateurs ?',
    answer: 'Absolument. TacticaFoot a été conçu pour tous les niveaux, du coach de district au directeur sportif professionnel. L\'interface intuitive permet de créer des tactiques en quelques minutes sans aucune compétence technique.',
  },
  {
    question: 'Puis-je tester TacticaFoot avant de m\'abonner ?',
    answer: 'Oui ! Vous pouvez créer un compte et découvrir les fonctionnalités de base. Les fonctionnalités avancées (export MP4, partage illimité, bibliothèque complète) sont disponibles avec un abonnement.',
  },
  {
    question: 'Comment exporter et partager mes créations ?',
    answer: 'TacticaFoot vous permet d\'exporter vos animations en vidéo MP4, en image PNG ou de les partager directement via un lien. Idéal pour envoyer vos séquences sur WhatsApp ou par email avant un entraînement.',
  },
  {
    question: 'TacticaFoot fonctionne-t-il sur mobile et tablette ?',
    answer: 'Oui, TacticaFoot est entièrement responsive et disponible en tant qu\'application PWA installable sur iOS et Android. Vous pouvez créer et consulter vos tactiques depuis n\'importe quel appareil.',
  },
  {
    question: 'Comment annuler mon abonnement ?',
    answer: 'Vous pouvez annuler votre abonnement à tout moment depuis votre espace personnel, sans frais ni pénalité. L\'accès reste actif jusqu\'à la fin de la période payée.',
  },
];

interface Props {
  preview?: boolean;
}

export const FAQSection: React.FC<Props> = ({ preview = true }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<number | false>(false);
  const displayed = preview ? FAQ_ITEMS.slice(0, 4) : FAQ_ITEMS;

  return (
    <Box
      component="section"
      id="faq"
      sx={{
        py: { xs: 7, md: 10 },
        px: { xs: 2, md: 4 },
        bgcolor: 'rgba(0,0,0,0.4)',
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: 1.5,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 700,
            color: 'white',
          }}
        >
          Questions fréquentes
        </Typography>
        <Typography
          align="center"
          sx={{ mb: 6, color: 'rgba(255,255,255,0.55)', fontSize: '1.05rem' }}
        >
          Tout ce que vous devez savoir avant de vous lancer
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {displayed.map((item, i) => (
            <Accordion
              key={i}
              expanded={expanded === i}
              onChange={() => setExpanded(expanded === i ? false : i)}
              elevation={0}
              sx={{
                bgcolor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px !important',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  borderColor: 'rgba(255,215,0,0.3)',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#FFD700' }} />}
                sx={{
                  px: 3,
                  py: 0.5,
                  '& .MuiAccordionSummary-content': { my: 1.5 },
                }}
              >
                <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '1rem' }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {preview && (
          <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/faq')}
              sx={{
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'rgba(255,255,255,0.8)',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.06)' },
                px: 4,
                py: 1.2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              Voir toutes les questions
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                bgcolor: '#2e7d32',
                '&:hover': { bgcolor: '#1b5e20' },
                px: 4,
                py: 1.2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              Poser ma question
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};
